import type { InsuranceStatus } from "@frontend/lib/actions/actions";
import { InsurancesStatusEnum } from "@frontend/lib/graphql/generated-gql";
import type { InsuranceErrorState } from "../../lib/insuranceErrorStates/insuranceErrorStates";
import type { SimpleInsuranceData } from "../actors/upload-consumer-photos.actor";
import type { InsuranceOutput } from "../contracts/arc-contracts";

export type InsuranceShape = {
  id?: string;
  payerId?: string;
  payer: string;
  memberId: string;
  state: string;
  status?: string;
  planName?: string;
  network?: string;
  priority?: number;
  subscriberName?: string;
  insuredFirstName?: string;
  insuredLastName?: string;
  hasBenefits?: boolean;
};

const VALID_STATUSES = new Set([
  InsurancesStatusEnum.Active,
  InsurancesStatusEnum.Manuallyverified,
]);

export function isInsuranceValid(ins: {
  status?: string;
  network?: string;
}): boolean {
  return (
    VALID_STATUSES.has(ins.status as InsurancesStatusEnum) &&
    ins.network?.toUpperCase() === "IN"
  );
}

const OON_STATUSES = new Set([InsurancesStatusEnum.ActiveOutOfNetwork]);

const UNVERIFIED_STATUSES = new Set([
  InsurancesStatusEnum.Unverified,
  InsurancesStatusEnum.ActiveBenefitsNotReturned,
]);

export type EligibilityFailureType = "oon" | "inactive" | "unverified";

/**
 * Map a reprice/eligibility result into the shape `context.insuranceStates.{primary,secondary}`
 * uses. Mirrors `mapInsuranceStatusToState` in `verify-insurance.actor.ts` so both the
 * verify-insurance path and the returning-patient savedInsurance path produce consistent state
 * — otherwise the saved-insurance path leaves `insuranceStates` undefined and
 * `isAtLeastPrimaryInsuranceActive` returns false, suppressing insured pricing.
 */
export function mapRepriceToInsuranceState(ins: {
  status?: string;
  network?: string;
  hasBenefits?: boolean;
}): {
  inNetwork: boolean;
  active: boolean;
  withBenefits: boolean;
  verified: boolean;
} {
  const result = {
    inNetwork: ins.network === "IN",
    active:
      ins.status === InsurancesStatusEnum.Active ||
      ins.status === InsurancesStatusEnum.ActiveBenefitsNotReturned,
    withBenefits: ins.hasBenefits === true,
    verified: ins.status !== InsurancesStatusEnum.Inactive,
  };
  return result;
}

export function deriveEligibilityFailureType(ins: {
  status?: string;
  network?: string;
}): EligibilityFailureType {
  if (
    OON_STATUSES.has(ins.status as InsurancesStatusEnum) ||
    ([
      InsurancesStatusEnum.Active,
      InsurancesStatusEnum.ActiveBenefitsNotReturned,
    ].includes(ins.status as InsurancesStatusEnum) &&
      ins.network?.toUpperCase() !== "IN")
  ) {
    return "oon";
  }
  if (
    (ins.status as InsurancesStatusEnum) === InsurancesStatusEnum.Unverified ||
    ((ins.status as InsurancesStatusEnum) ===
      InsurancesStatusEnum.ActiveBenefitsNotReturned &&
      ins.network?.toUpperCase() === "IN")
  ) {
    return "unverified";
  }
  return "inactive";
}

export function computeRepriceOutcome(
  primary: { status?: string; network?: string },
  secondary?: { status?: string; network?: string } | null,
): "success" | "failure" {
  const allValid =
    isInsuranceValid(primary) && (!secondary || isInsuranceValid(secondary));
  return allValid ? "success" : "failure";
}

export function pickInsuranceFields(ins: InsuranceShape): {
  insuranceId?: string;
  payerId?: string;
  payer: string;
  memberId: string;
  state: string;
  subscriberName?: string;
  planName?: string;
  status?: string;
  network?: string;
  insuredFirstName?: string;
  insuredLastName?: string;
  hasBenefits?: boolean;
} {
  return {
    insuranceId: ins.id,
    payerId: ins.payerId,
    payer: ins.payer,
    memberId: ins.memberId,
    state: ins.state,
    subscriberName: ins.subscriberName,
    planName: ins.planName,
    status: ins.status,
    network: ins.network,
    insuredFirstName: ins.insuredFirstName,
    insuredLastName: ins.insuredLastName,
    hasBenefits: ins.hasBenefits,
  };
}

type PickedInsurance = ReturnType<typeof pickInsuranceFields>;

/**
 * Assemble the input for the insurance arc from the parent machine's context.
 *
 * Three data sources (checked in priority order):
 *   1. Re-invocation (_insuranceOutput exists) → bare baseInput, no savedInsurance
 *   2. Fresh reprice (_returningPatientPrimaryInsurance) → savedInsurance with computed outcome
 *   3. Stale match data (context.insurances.primaryInsurance) → savedInsurance without outcome
 *   4. No data → bare baseInput
 */
// biome-ignore lint/suspicious/noExplicitAny: Accepts wide KioskMachineContext from parent machine
export function assembleInsuranceArcInput(context: any) {
  const baseInput = {
    orgId: context.orgId as number,
    consumerId: context.consumerId,
    firstName: context.firstName as string,
    lastName: context.lastName as string,
    dateOfBirth: context.dateOfBirth as string,
    email: context.email as string | undefined,
    addingInsuranceFromStorefront: context.addingInsuranceFromStorefront as
      | boolean
      | undefined,
    previousOutput: context._insuranceOutput as InsuranceOutput | undefined,
    qrMode: context.qrMode as boolean | undefined,
  };

  // Re-invocation: insurance arc already has its own output, no savedInsurance needed
  if (context._insuranceOutput) {
    return baseInput;
  }

  const repricePrimary = context._returningPatientPrimaryInsurance as
    | InsuranceShape
    | null
    | undefined;
  const repriceSecondary = context._returningPatientSecondaryInsurance as
    | InsuranceShape
    | null
    | undefined;

  // Fresh reprice data available — compute outcome from status/network
  if (repricePrimary) {
    const outcome = computeRepriceOutcome(repricePrimary, repriceSecondary);
    const primaryFields = pickInsuranceFields(repricePrimary);
    const secondaryFields = repriceSecondary
      ? pickInsuranceFields(repriceSecondary)
      : undefined;
    // Reprice responses don't include kiosk DB insurance IDs. Carry them forward
    // from stale match data (context.insurances) only when memberId matches, so
    // createInsuranceActor can update the correct existing record.
    const staleInsurances = context.insurances as
      | {
          primaryInsurance?: { memberId?: string; insuranceId?: string };
          secondaryInsurance?: { memberId?: string; insuranceId?: string };
        }
      | undefined;
    const resolvedPrimaryId =
      primaryFields.insuranceId ??
      (staleInsurances?.primaryInsurance?.memberId?.toLowerCase() ===
      repricePrimary.memberId?.toLowerCase()
        ? staleInsurances?.primaryInsurance?.insuranceId
        : undefined);
    const resolvedSecondaryId =
      secondaryFields?.insuranceId ??
      (repriceSecondary &&
      staleInsurances?.secondaryInsurance?.memberId?.toLowerCase() ===
        repriceSecondary.memberId?.toLowerCase()
        ? staleInsurances?.secondaryInsurance?.insuranceId
        : undefined);
    return {
      ...baseInput,
      savedInsurance: {
        primaryInsurance: { ...primaryFields, insuranceId: resolvedPrimaryId },
        primaryInsurancePhotoFront: context.primaryInsurancePhotoFront as
          | string
          | undefined,
        secondaryInsurance: secondaryFields
          ? { ...secondaryFields, insuranceId: resolvedSecondaryId }
          : undefined,
        secondaryInsurancePhotoFront: context.secondaryInsurancePhotoFront as
          | string
          | undefined,
        savedInsuranceOutcome: outcome,
      },
    };
  }

  // Stale match data (reprice not yet back) — no outcome, routes to confirmation screens
  if (context.insurances?.primaryInsurance) {
    return {
      ...baseInput,
      savedInsurance: {
        primaryInsurance: context.insurances
          .primaryInsurance as PickedInsurance,
        primaryInsurancePhotoFront: context.primaryInsurancePhotoFront as
          | string
          | undefined,
        secondaryInsurance: context.insurances.secondaryInsurance as
          | PickedInsurance
          | undefined,
        secondaryInsurancePhotoFront: context.secondaryInsurancePhotoFront as
          | string
          | undefined,
      },
    };
  }

  return baseInput;
}

/**
 * Build the insurance array for the photo upload actor input.
 *
 * PLA-1343: Only include insurances that have an insuranceId.
 * Secondary insurance may exist in context (payer/memberId/state from the
 * insurance arc) but never received an insuranceId because createInsurance
 * skipped it (e.g. missing payerId). Sending it without insuranceId causes
 * SimpleInsuranceDataSchema validation to fail.
 */
export function buildInsuranceArrayForPhotoUpload(
  insurances:
    | {
        primaryInsurance?: Record<string, unknown>;
        secondaryInsurance?: Record<string, unknown>;
      }
    | undefined,
  isWorkersComp: boolean,
): SimpleInsuranceData[] {
  if (isWorkersComp) return [];

  return [
    insurances?.primaryInsurance?.insuranceId && {
      ...insurances.primaryInsurance,
      priority: 1,
    },
    insurances?.secondaryInsurance?.insuranceId && {
      ...insurances.secondaryInsurance,
      priority: 2,
    },
  ].filter(Boolean) as SimpleInsuranceData[];
}

/**
 * Maps a raw eligibility service result into the InsuranceErrorState shape used
 * by insuranceStates.{primary,secondary}. Counterpart to mapRepriceToInsuranceState
 * which maps reprice data from the returning-patient background check.
 */
export function mapInsuranceStatusToState(
  insuranceStatus: InsuranceStatus,
): InsuranceErrorState {
  const status = insuranceStatus.status;
  return {
    inNetwork: insuranceStatus.network === "IN",
    active:
      status === InsurancesStatusEnum.Active ||
      status === InsurancesStatusEnum.ActiveBenefitsNotReturned,
    withBenefits: insuranceStatus.hasBenefits,
    verified: status !== InsurancesStatusEnum.Inactive,
  };
}

/**
 * Decides whether to carry the actor's pendingPricesMode into context.
 *
 * Gate: if primary.withBenefits is true, benefits are fully available so pricing
 * can run immediately — return false. Otherwise preserve the actor's value.
 *
 * Using withBenefits (not active) as the gate correctly handles BNR: BNR has
 * active:true (confirmed insurance) but withBenefits:false (no benefit detail),
 * so pendingPricesMode must remain true for the storefront to show pending pricing.
 */
export function derivePendingPricesMode(
  insuranceStates: { primary?: { withBenefits?: boolean } } | undefined,
  actorPendingPricesMode: boolean | undefined,
): boolean {
  if (insuranceStates?.primary?.withBenefits === true) return false;
  return actorPendingPricesMode ?? false;
}

type InsuranceInputFields = {
  payerId?: string | null;
  ehrInsuranceId?: string | null;
  memberId: string;
};

/**
 * Builds a normalized insurance result object by merging eligibility service data
 * with the original input fields (payerId, ehrInsuranceId) and optionally backfilling
 * memberId from coverageOverview when OCR couldn't read it from the card (PLA-1343).
 */
export function buildInsuranceResult(
  insuranceStatus: InsuranceStatus,
  insuranceInput: InsuranceInputFields,
  insuranceDataId: string | undefined,
): {
  payerId: string | null | undefined;
  ehrInsuranceId: string | null | undefined;
  active: boolean;
  verified: boolean;
  insuranceId?: string;
} & InsuranceStatus {
  const status = insuranceStatus.status;

  const extendedStatus = insuranceStatus as InsuranceStatus & {
    coverageOverview?: { memberId?: string };
  };
  const memberId =
    insuranceStatus.memberId ||
    extendedStatus.coverageOverview?.memberId ||
    insuranceInput.memberId;

  // Normalize ACTIVE + hasBenefits:false → ACTIVE_BENEFITS_NOT_RETURNED.
  // Some providers (e.g. AIR service force-status TX-BNR) return status=ACTIVE
  // with hasBenefits=false to represent the BNR scenario. Production payers
  // return ACTIVE_BENEFITS_NOT_RETURNED directly. Normalizing here ensures a
  // consistent status is written to the DB regardless of which path produced it.
  const effectiveStatus =
    status === InsurancesStatusEnum.Active && !insuranceStatus.hasBenefits
      ? InsurancesStatusEnum.ActiveBenefitsNotReturned
      : status;

  return {
    payerId: insuranceInput.payerId,
    ehrInsuranceId: insuranceInput.ehrInsuranceId,
    active:
      effectiveStatus === InsurancesStatusEnum.Active ||
      effectiveStatus === InsurancesStatusEnum.ActiveBenefitsNotReturned,
    verified: effectiveStatus !== InsurancesStatusEnum.Inactive,
    ...(insuranceDataId && { insuranceId: insuranceDataId }),
    ...insuranceStatus,
    status: effectiveStatus,
    memberId,
  };
}
